import { Link } from 'react-router'
import { useAppSelector } from "../hooks/reduxToolkit"
import { ExpenseModel, isTextractJobFailed, isTextractPending } from "../reduxToolkit/expensesSlice"
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone"
import { formatCurrency } from "../../lib/formatCurrency"
import { present } from "../../lib/util/present"
import { Tooltip } from "./tooltip"
import { useCustomization } from "../hooks/useCustomizations"
import { hraCardDisplayLabel } from "../reduxToolkit/selectors/customizations"
import { useTerminology } from "../hooks/useTerminology"
import { IncidentLineItem } from "./expenseTable"
import { deriveExpenseState, expenseStateBadge } from "../../lib/expenses/expenseState"
import { BankChargeChip, isPlaidCharge } from "./bankChargeChip"

import './triageExpenseRow.scss'

const SECONDS = 1000

export interface TriageExpenseRowProps {
  expense: ExpenseModel
  /** Ids of closed incidents, used to derive this expense's state. */
  closedIncidentIds: Set<string>
  isSelectMode: boolean
  isSelected: boolean
  isHighlighted?: boolean
  onToggleSelected: (id: string) => void
  /** Request deleting this expense. */
  onRequestDelete?: (expense: ExpenseModel) => void
}

export function TriageExpenseRow({
  expense,
  closedIncidentIds,
  isSelectMode,
  isSelected,
  isHighlighted,
  onToggleSelected,
  onRequestDelete,
}: TriageExpenseRowProps) {
  const hra = useCustomization('hra')
  const terms = useTerminology()

  const hasDuplicateTodo = useAppSelector((s) =>
    s.todos.todos?.some((t) => t.record_id === expense.id && t.todo_type === 'duplicate-detected' && !t.completed_at)
  )

  const textractPending = isTextractPending(expense)
  const textractFailed = isTextractJobFailed(expense)

  const state = deriveExpenseState(expense, closedIncidentIds)
  const stateBadge = expenseStateBadge(state, expense)
  const cardCharges = (expense.payment_history?.payments ?? []).filter(isPlaidCharge)

  let alert: JSX.Element | undefined
  if (textractPending && !textractFailed) {
    alert = (
      <Tooltip tooltip="Scanning expense to extract information. This can sometimes take a few minutes.">
        <i className="material-icons flicker">sensors</i>
      </Tooltip>
    )
  } else if (textractFailed) {
    alert = (
      <Tooltip tooltip="Unable to extract expense information">
        <i className="material-icons text-danger">warning</i>
      </Tooltip>
    )
  } else if (hasDuplicateTodo) {
    alert = (
      <Tooltip tooltip="This expense may be a duplicate. Review and delete if it is, or mark it as not a duplicate in the edit screen.">
        <i className="material-icons text-warning">content_copy</i>
      </Tooltip>
    )
  } else if (!textractPending && !textractFailed && Date.parse(expense.created_at) < (Date.now() - 10 * SECONDS)) {
    // Show warning if expense has been around a while but is still incomplete (missing key fields)
    const isMissingFields = !present(expense.date) || !present(expense.paidAmount)
    if (isMissingFields) {
      alert = (
        <Tooltip tooltip="This expense is missing some information">
          <i className="material-icons text-danger">warning</i>
        </Tooltip>
      )
    }
  }

  const providerHref = expense.provider_id
    ? `/providers/${expense.provider_id}`
    : `/expenses/${expense.id}/providers/new`

  const classes = [
    'triage-expense-row col-12 card',
    // The --draft accent (left border color) and the bare .draft class (reduced
    // opacity) are two distinct, intentionally co-existing treatments for the
    // same state, not a legacy/new duplicate — expenseTable.tsx also applies its
    // own scoped .draft class for the same opacity effect in the detail view.
    `triage-expense-row--${state}`,
    state === 'draft' && 'draft',
    isSelected && 'selected',
    isHighlighted && 'highlighted',
  ].filter(present)

  return (
    <div className="row" id={`triage-expense-row_${expense.id}`}>
      <div className={classes.join(' ')}>
        <div className="card-body">
          <div className="triage-expense-row__header">
            {isSelectMode && (
              <input
                type="checkbox"
                className="form-check-input triage-expense-row__checkbox me-2"
                checked={isSelected}
                onChange={() => onToggleSelected(expense.id)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select expense from ${expense.provider || 'unknown provider'}`}
              />
            )}
            <div className="triage-expense-row__main flex-grow-1">
              <div className="triage-expense-row__title-row d-flex align-items-center gap-2">
                <h5 className="triage-expense-row__provider mb-0">
                  {present(expense.provider) ? (
                    <Link to={providerHref} className="triage-expense-row__provider-link">
                      {expense.provider}
                    </Link>
                  ) : (
                    <Link to={providerHref} className="text-muted">
                      No provider
                    </Link>
                  )}
                </h5>
                <div className="triage-expense-row__badges d-flex align-items-center gap-1">
                  <span className={`badge triage-expense-row__state-badge bg-${stateBadge.variant}`}>
                    {stateBadge.label}
                  </span>
                  {expense.paid_with_hra && (
                    <Tooltip tooltip={`This expense was paid with your HRA card.`}>
                      <span className="badge bg-secondary">{hraCardDisplayLabel(hra, 'HRA CARD')}</span>
                    </Tooltip>
                  )}
                  {/* Redux Persist guard: old persisted expenses lack auto_categorized_at — truthiness treats undefined as not-auto-categorized */}
                  {expense.auto_categorized_at && (
                    <Tooltip tooltip={`Alice automatically assigned this expense to one of your ${terms.incidents}. If it looks wrong, edit it to reassign.`}>
                      <span className="badge bg-info triage-expense-row__alice-badge">
                        <i className="material-icons">support_agent</i> Alice
                      </span>
                    </Tooltip>
                  )}
                  {/* Recurring-series flagged drafts are never auto_categorized (only individually
                      assigned drafts get that stamp), so without this badge they'd look like any
                      other untouched draft once the member dismisses the summary notification.
                      Redux Persist guard: old persisted expenses lack auto_categorization. */}
                  {!expense.auto_categorized_at && expense.auto_categorization?.recurring_series && (
                    <Tooltip tooltip="Alice noticed these charges may be a payment plan. Open the earliest one to confirm.">
                      <span className="badge bg-warning triage-expense-row__alice-badge">
                        <i className="material-icons">repeat</i> Payment plan?
                      </span>
                    </Tooltip>
                  )}
                  {alert}
                </div>
                <div className="triage-expense-row__actions ms-auto d-flex align-items-center gap-2">
                  {state === 'draft' && present(expense.plaid_transaction_id) && (
                    <Link
                      to={`/expenses/${expense.id}/link-to-expense`}
                      className="btn btn-sm btn-link triage-expense-row__link-link"
                      aria-label={`Link expense${expense.provider ? ` from ${expense.provider}` : ''} to an existing expense`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Link
                    </Link>
                  )}
                  <Link
                    to={`/expenses/${expense.id}/edit`}
                    className="btn btn-sm btn-link triage-expense-row__edit-link"
                  >
                    {state === 'draft' ? 'Create' : 'Edit'}
                  </Link>
                  {onRequestDelete && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger triage-expense-row__delete-link"
                      aria-label={`Delete expense${expense.provider ? ` from ${expense.provider}` : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRequestDelete(expense)
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div className="triage-expense-row__meta small text-muted mt-1">
                {expense.date
                  ? formatDateInTimeZone(expense.date, { format: 'MM/dd/yyyy' })
                  : 'Unknown date'}
                {' · '}
                {expense.paidAmount ? formatCurrency(expense.paidAmount) : 'Unknown amount'}
                {present(expense.patient_name) && ` · ${expense.patient_name}`}
              </div>
              {cardCharges.length > 0 && (
                <div className="triage-expense-row__charges mt-1 d-flex flex-wrap gap-1">
                  {cardCharges.map((payment, i) => (
                    <BankChargeChip
                      key={payment.plaid_transaction_id ?? `charge-${payment.date}-${payment.amount}-${i}`}
                      payment={payment}
                    />
                  ))}
                </div>
              )}
              <div className="triage-expense-row__incident mt-1">
                {present(expense.incident_id) ? (
                  <IncidentLineItem expense={expense} />
                ) : (
                  <span className="text-muted small">No {terms.incident} assigned</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
